import { useEffect, useRef } from 'react';
import { useAuth } from '@/hooks/useAuth';

declare global {
  interface Window {
    $crisp: any[];
    CRISP_WEBSITE_ID: string;
  }
}

const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 3000;

const loadCrispScript = (onSuccess: () => void, onError: (attempt: number) => void, attempt = 1) => {
  const script = document.createElement("script");
  script.src = "https://client.crisp.chat/l.js";
  script.async = true;

  script.onload = onSuccess;
  script.onerror = () => {
    if (attempt < MAX_RETRIES) {
      setTimeout(() => {
        loadCrispScript(onSuccess, onError, attempt + 1);
      }, RETRY_DELAY_MS);
    } else {
      onError(attempt);
    }
  };

  document.head.appendChild(script);
};

const CrispChat = () => {
  const { currentUser } = useAuth();
  const isLoadedRef = useRef(false);

  // Load Crisp script only once after auth is initialized
  useEffect(() => {
    if (isLoadedRef.current || typeof window === 'undefined') return;

    if (!window.CRISP_WEBSITE_ID) {
      window.$crisp = [];
      window.CRISP_WEBSITE_ID = "74537b53-249a-404d-8677-c1cca78388dd";

      loadCrispScript(
        () => {
          isLoadedRef.current = true;

          window.$crisp.push(["set", "color:theme", "#9b87f5"]);
          window.$crisp.push(["set", "website:name", "BattleforgePC"]);
          window.$crisp.push(["set", "message:text", [
            ["Welcome to BattleforgePC! 🎮 How can we help you today?"]
          ]]);
          window.$crisp.push(["do", "chat:show"]);

          if (currentUser?.email) {
            window.$crisp.push(["set", "user:email", currentUser.email]);
            window.$crisp.push(["set", "user:nickname", currentUser.email.split('@')[0]]);
            window.$crisp.push(["set", "session:data", [["User ID", currentUser.id]]]);
          }
        },
        (attempt) => {
          console.warn(`Crisp chat failed to load after ${attempt} attempts.`);
        }
      );
    }
  }, [currentUser]);

  // Sync user info on auth change *after* Crisp is ready
  useEffect(() => {
    if (!isLoadedRef.current || !window?.$crisp) return;

    if (currentUser?.email) {
      try {
        window.$crisp.push(["set", "user:email", currentUser.email]);
        window.$crisp.push(["set", "user:nickname", currentUser.email.split('@')[0]]);
        window.$crisp.push(["set", "session:data", [["User ID", currentUser.id]]]);
      } catch (err) {
        console.warn("Crisp user push error:", err);
      }
    } else {
      try {
        window.$crisp.push(["do", "session:reset"]);
      } catch (err) {
        console.warn("Crisp session reset error:", err);
      }
    }
  }, [currentUser]);

  return null;
};

export default CrispChat;
